import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Navbar from '../../components/Navbar/Navbar';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { db } from '../../firebase';
import { 
  Gavel, 
  Scale, 
  Sparkles, 
  Coins, 
  ChevronRight, 
  ArrowRight,
  Lock,
  EyeOff
} from 'lucide-react';
import './HomePage.css';

const HomePage = () => {
  const [recentCases, setRecentCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [interactiveVote, setInteractiveVote] = useState(null);
  const [simulatedTallies, setSimulatedTallies] = useState({
    guilty: 74,
    jail: 18,
    justified: 8
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchRecentCases = async () => {
      try {
        const casesRef = collection(db, 'cases');
        const q = query(casesRef, orderBy('createdAt', 'desc'), limit(3));
        const snap = await getDocs(q);

        let list = [];
        snap.forEach(doc => {
          list.push({ id: doc.id, ...doc.data() });
        });

        // Only load from cases collection (excludes legacy AskAway queries)
        setRecentCases(list);
      } catch (err) {
        console.warn('Error fetching recent cases for landing:', err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentCases();
  }, []);

  const handleSimulatedVote = (choice) => {
    setInteractiveVote(choice);
    setSimulatedTallies(prev => {
      if (choice === 'guilty') return { ...prev, guilty: prev.guilty + 1 };
      if (choice === 'jail') return { ...prev, jail: prev.jail + 1 };
      if (choice === 'justified') return { ...prev, justified: prev.justified + 1 };
      return prev;
    });
  };

  return (
    <div className="home-page-wrapper">
      <Navbar />

      {/* Hero Section */}
      <section className="court-hero-section">
        <div className="hero-atmosphere"></div>
        <div className="hero-content">
          
          <div className="hero-tag-pill">
            <span className="live-indicator"></span>
            <Gavel size={14} className="tag-gavel" />
            <span>High Court of Petty Grievances & Life Disasters</span>
          </div>

          <h1 className="hero-headline">
            THE KANGAROO COURT
            <span className="hero-subheadline">& The Vault of Shame</span>
          </h1>

          <p className="hero-manifesto">
            The private disaster tribunal for close circles. Where unhinged client requests, architectural fails, 
            medical chaos, and friend blunders face peer jury roasts, merciless AI Magistrate decrees, and permanent infamy.
          </p>

          <div className="hero-action-row">
            <Link to="/browse" className="btn-enter-court">
              <Scale size={18} />
              <span>Enter The Docket</span>
              <ArrowRight size={16} />
            </Link>
            <Link to="/post" className="btn-file-seal">
              <EyeOff size={18} />
              <span>File Case Under Seal (+25 Chips)</span>
            </Link>
          </div>
        </div>

        {/* Live Interactive Courtroom Showcase */}
        <div className="courtroom-stage-card">
          <div className="stage-top-bar">
            <div className="stage-badge-group">
              <span className="live-pulse-badge">
                <span className="pulse-dot"></span> LIVE TRIAL
              </span>
              <span className="docket-number-badge">CASE #042</span>
              <span className="discipline-tag-stage">#DesignDisaster</span>
            </div>
            <span className="jury-attendance">⚖️ 88 Jurors Deliberating</span>
          </div>

          <div className="stage-case-body">
            <h3 className="stage-case-title">
              "Senior Designer committed Comic Sans directly to the production executive board deck"
            </h3>
            <p className="stage-case-evidence">
              <strong>Exhibit A:</strong> 48 slides submitted to the Board of Directors. Page 12 revenue forecast header 
              was rendered in bright purple Comic Sans Bold. Defendant claims: <em>"It added warmth and approachable energy to the EBITDA projections."</em>
            </p>

            {/* Sarcastic Magistrate Decree */}
            <div className="stage-magistrate-decree">
              <div className="decree-header">
                <Gavel size={16} className="decree-gavel" />
                <span className="decree-title">Sarcastic Magistrate Verdict Decree</span>
                <span className="magistrate-flair">Gordon Ramsay persona</span>
              </div>
              <p className="decree-quote">
                "The Court has reviewed the slides. It didn't add 'warmth'—it added visual arson. 
                I have seen highway roadkill with tighter typographic hierarchy. The Court sentences the defendant 
                to 14 days of mandatory black-and-white Helvetica and one cold brew tribute to the on-call team."
              </p>
            </div>

            {/* Interactive Verdict Deliberation */}
            <div className="stage-jury-verdict-box">
              <span className="jury-prompt">Cast Simulated Verdict:</span>
              <div className="verdict-buttons-group">
                <button 
                  onClick={() => handleSimulatedVote('guilty')}
                  className={`btn-verdict-chip ${interactiveVote === 'guilty' ? 'selected' : ''}`}
                >
                  <span>Guilty ({simulatedTallies.guilty}%)</span>
                </button>
                <button 
                  onClick={() => handleSimulatedVote('jail')}
                  className={`btn-verdict-chip danger ${interactiveVote === 'jail' ? 'selected' : ''}`}
                >
                  <span>Straight to Jail ({simulatedTallies.jail}%)</span>
                </button>
                <button 
                  onClick={() => handleSimulatedVote('justified')}
                  className={`btn-verdict-chip muted ${interactiveVote === 'justified' ? 'selected' : ''}`}
                >
                  <span>Justified ({simulatedTallies.justified}%)</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Asymmetric Bento Grid of Tribunal Mechanics */}
      <section className="tribunal-bento-section">
        <div className="bento-section-header">
          <span className="section-label">Judicial Architecture</span>
          <h2>How The Syndicate Operates</h2>
          <p>Four distinct mechanics turning daily professional chaos into high-stakes entertainment.</p>
        </div>

        <div className="bento-grid">
          {/* Card 1 (Wide): Sarcastic AI Magistrate */}
          <div className="bento-card bento-wide bento-magistrate">
            <div className="bento-card-inner">
              <div className="bento-badge-row">
                <span className="bento-chip gold">The AI Bench</span>
                <Sparkles size={16} className="sparkle-icon" />
              </div>
              <h3>Gordon Ramsay Meets Saul Goodman</h3>
              <p>
                Every case filed can summon our theatrical AI Magistrate. The Judge parses screenshots, emails, 
                and chat receipts, roasts everyone involved without filter, and hands down absurd penalties.
              </p>
              <div className="bento-mini-demo">
                <div className="demo-chip">⚖️ "Defendant is guilty of gross incompetence in the first degree."</div>
              </div>
            </div>
          </div>

          {/* Card 2: The Sunday Vault of Shame */}
          <div className="bento-card bento-vault">
            <div className="bento-card-inner">
              <div className="bento-badge-row">
                <span className="bento-chip crimson">Permanent Infamy</span>
                <Lock size={16} />
              </div>
              <h3>The Sunday Vault</h3>
              <p>
                Every Sunday at 11:59 PM, the top peer-voted disaster is permanently sealed into the Vault of Shame. 
                Immortal group infamy.
              </p>
              <div className="vault-countdown-box">
                <span className="countdown-label">Next Vault Drop</span>
                <span className="countdown-val">Sunday • 23:59 IST</span>
              </div>
            </div>
          </div>

          {/* Card 3: The Bail & Contempt Economy */}
          <div className="bento-card bento-bail">
            <div className="bento-card-inner">
              <div className="bento-badge-row">
                <span className="bento-chip amber">Real Stakes</span>
                <Coins size={16} />
              </div>
              <h3>Bail & Contempt Store</h3>
              <p>
                Deliberate on cases to earn Court Chips. Spend them in the store to slap Clown Badges on friends 
                or demand real iced coffee tributes.
              </p>
              <div className="chips-bounty-preview">
                <span className="bounty-item">🤡 Clown of Week (100 pts)</span>
                <span className="bounty-item">☕ Iced Coffee Ransom (250 pts)</span>
              </div>
            </div>
          </div>

          {/* Card 4 (Wide): Cryptographic Anonymous Seal */}
          <div className="bento-card bento-wide bento-seal">
            <div className="bento-card-inner">
              <div className="bento-badge-row">
                <span className="bento-chip emerald">Whistleblower Protection</span>
                <EyeOff size={16} />
              </div>
              <h3>Zero-Trace Anonymous Filing</h3>
              <p>
                File cases under full cryptographic seal. Your identity is stripped from the database doc and 
                redacted from peer jurors. Spill unhinged client drama without professional blowback.
              </p>
              <div className="seal-toggle-preview">
                <div className="simulated-toggle active">
                  <span className="toggle-indicator"></span>
                  <span className="toggle-text">🛡️ Anonymous Seal: ACTIVE (Identity Hidden)</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Live Docket Spotlight */}
      <section className="live-spotlight-section">
        <div className="spotlight-header-row">
          <div>
            <span className="section-label">Current Filings</span>
            <h2>Active on the Docket</h2>
          </div>
          <Link to="/browse" className="view-all-docket-link">
            <span>Browse Full Docket</span>
            <ChevronRight size={16} />
          </Link>
        </div>

        {loading ? (
          <div className="spotlight-loading">
            <div className="loading-spinner"></div>
            <p>Summoning open tribunal records...</p>
          </div>
        ) : recentCases.length > 0 ? (
          <div className="spotlight-cards-grid">
            {recentCases.map((c, i) => (
              <div 
                key={c.id} 
                className="spotlight-case-card"
                onClick={() => navigate(`/query/${c.id}`)}
              >
                <div className="spotlight-badge-row">
                  <span className="spotlight-case-num">Case #{c.caseNumber || (101 + i)}</span>
                  <span className="spotlight-cat">{c.category || (c.tags && c.tags[0]) || '#Grievance'}</span>
                </div>
                <h4>{c.title}</h4>
                <p>{c.description ? c.description.substring(0, 130) + '...' : 'Evidence on file.'}</p>
                <div className="spotlight-footer">
                  <span className="author-seal">{c.isAnonymous ? '🛡️ Filed Under Seal' : `By ${c.authorAlias || 'Juror'}`}</span>
                  <span className="spotlight-votes">
                    <Scale size={14} />
                    {c.totalVotes || c.voteCount || 0} Votes
                  </span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="spotlight-empty-box">
            <Gavel size={32} className="empty-gavel" />
            <p>The docket is currently clear. Be the first to bring an unhinged case to trial!</p>
            <Link to="/post" className="btn-empty-start">File Case (+25 Chips)</Link>
          </div>
        )}
      </section>

      {/* Footer */}
      <footer className="court-footer">
        <div className="footer-inner">
          <div className="footer-brand">
            <span className="footer-logo">⚖️ The Kangaroo Court</span>
            <p>Private Disaster Syndicate • Peer Justice • The Vault of Shame</p>
          </div>
          <div className="footer-nav">
            <Link to="/browse">The Docket</Link>
            <Link to="/post">File Case</Link>
            <Link to="/rewards">Bail Store</Link>
            <Link to="/terms">Court Statutes</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;
